/**
 * Official Indus Viva catalog for the iLeafs verified reseller storefront.
 * Features high-resolution studio packshots and verified product specs.
 */
import type { LiveProduct } from '../lib/indusvivaApi';

export type CatalogProduct = {
  id: number;
  slug: string;
  name: string;
  tagline: string;
  price: number;
  pv: number;
  image: string;
  studioImage?: string;
  originalImage: string;
  category: string;
  categorySlug: string;
  rating: number;
  reviewCount: number;
  description: string;
  highlights: string[];
  dosage: string;
  badge?: string;
  order: number;
};

export const indusVivaProducts: CatalogProduct[] = [
  {
    id: 9,
    slug: 'ipulse',
    name: 'iPulse',
    tagline: 'Premium Antioxidant Superfruit Wellness Drink',
    price: 2997.5,
    pv: 25,
    image: '/indusviva/products/ipulse-studio.jpg',
    studioImage: '/indusviva/products/ipulse-studio.jpg',
    originalImage: '/indusviva/products/ipulse.jpeg',
    category: 'Antioxidant & Immunity',
    categorySlug: 'antioxidants',
    rating: 4.9,
    reviewCount: 342,
    badge: 'Bestseller',
    description:
      'iPulse is a proprietary superfruit wellness beverage featuring pure Acai berry alongside 14 other exotic fruits. Delivering a massive natural ORAC score, iPulse protects cells against oxidative stress, supports cardiovascular health, and boosts whole-body vitality.',
    highlights: [
      'Pure Acai Berry from the Amazon basin and 14 exotic superfruits',
      'Clinically recognized ORAC antioxidant value for whole-body defense',
      'Supports healthy cardiovascular and immune system functioning',
      '100% natural, vegetarian, free from artificial colors or synthetics',
    ],
    dosage: '30 ml twice daily, 30 minutes before meals (morning and evening).',
    order: 1,
  },
  {
    id: 443,
    slug: 'icare',
    name: 'iCare',
    tagline: 'Comprehensive Women’s Hormonal & Bone Health Drink',
    price: 2997.5,
    pv: 25,
    image: '/indusviva/products/icare-studio.jpg',
    studioImage: '/indusviva/products/icare-studio.jpg',
    originalImage: '/indusviva/products/icare.jpeg',
    category: 'Women’s Wellness',
    categorySlug: 'women-wellness',
    rating: 4.9,
    reviewCount: 289,
    badge: 'Award Winner',
    description:
      'iCare is a specialized wellness formulation crafted exclusively for women. Fortified with standardized extracts of Curcumin, Pomegranate, and Shatavari (Asparagus racemosus), it supports hormonal balance, bone density, stress resilience, and youthful vitality across every stage of womanhood.',
    highlights: [
      'Ayurvedic synergy of Shatavari, Curcumin, and natural Pomegranate',
      'Supports endocrine harmony, menstrual comfort, and mood balance',
      'Promotes bone mineral density and joint flexibility',
      'Delivers targeted natural antioxidants for women’s wellness',
    ],
    dosage: '30 ml twice daily, 30 minutes before food.',
    order: 2,
  },
  {
    id: 454,
    slug: 'icharge',
    name: 'iCharge',
    tagline: 'Natural Vitality, Stamina & Cognitive Energy Drink',
    price: 5995,
    pv: 25,
    image: '/indusviva/products/icharge-studio.jpg',
    studioImage: '/indusviva/products/icharge-studio.jpg',
    originalImage: '/indusviva/products/icharge.jpeg',
    category: 'Energy & Vitality',
    categorySlug: 'energy',
    rating: 4.8,
    reviewCount: 174,
    badge: 'High Potency',
    description:
      'iCharge is a breakthrough natural energizer powered by Kanna (Sceletium tortuosum), Ashwagandha, and botanical bio-nutrients. It revitalizes physical endurance, elevates cognitive focus, and combats fatigue without jitters, palpitations, or the dreaded sugar crash.',
    highlights: [
      'Clinically formulated with Kanna adaptogen and Ashwagandha',
      'Sustained clean energy without synthetic caffeine spikes',
      'Enhances physical endurance, mental acuity, and stress resilience',
      'Ideal for active lifestyles, professionals, and fitness enthusiasts',
    ],
    dosage: '1 bottle (can) daily mid-morning or 30 minutes prior to exertion.',
    order: 3,
  },
  {
    id: 421,
    slug: 'icoffee-black',
    name: 'iCoffee Black',
    tagline: 'Artisan Premium Coffee for Healthy Glucose Balance',
    price: 2997.5,
    pv: 25,
    image: '/indusviva/products/icoffee-black-studio.jpg',
    studioImage: '/indusviva/products/icoffee-black-studio.jpg',
    originalImage: '/indusviva/products/icoffee-black.jpeg',
    category: 'Metabolic & Sugar Balance',
    categorySlug: 'sugar-balance',
    rating: 4.9,
    reviewCount: 412,
    badge: 'Popular Choice',
    description:
      'iCoffee Black brings together premium roasted shade-grown Coorg coffee beans and standardized extracts of Salacia reticulata. Designed to assist carbohydrate management and maintain healthy blood sugar levels without altering the authentic rich coffee experience.',
    highlights: [
      'Standardized Salacia reticulata (Salacinol / Charantin extract)',
      'Aids in natural glycemic control and carbohydrate metabolism',
      'Artisan roasted Coorg coffee with robust taste and aroma',
      'Convenient single-serve sachets; zero sugar added',
    ],
    dosage: 'Dissolve 1 sachet in 100-120 ml of hot water. Drink 30 minutes before meals twice daily.',
    order: 4,
  },
  {
    id: 418,
    slug: 'icoffee-creamer',
    name: 'iCoffee Creamer',
    tagline: 'Smooth & Creamy Coffee Formulation for Sugar Balance',
    price: 2997.5,
    pv: 25,
    image: '/indusviva/products/icoffee-creamer.jpeg',
    studioImage: '/indusviva/products/icoffee-creamer.jpeg',
    originalImage: '/indusviva/products/icoffee-creamer.jpeg',
    category: 'Metabolic & Sugar Balance',
    categorySlug: 'sugar-balance',
    rating: 4.8,
    reviewCount: 236,
    badge: 'Customer Favorite',
    description:
      'iCoffee Creamer delivers the therapeutic benefits of Salacia reticulata combined with a velvety, smooth non-dairy creamer. Specially formulated for coffee lovers who prefer a creamy latte while keeping their glucose levels balanced.',
    highlights: [
      'Infused with bio-active Salacia reticulata polyphenols',
      'Smooth, creamy latte mouthfeel with non-dairy plant creamer',
      'Naturally curbs post-meal glycemic spikes',
      'Simple instant preparation at home, work, or travel',
    ],
    dosage: 'Mix 1 sachet with hot water (120 ml). Stir thoroughly and consume 30 minutes before meals.',
    order: 5,
  },
  {
    id: 405,
    slug: 'iglow',
    name: 'iGlow',
    tagline: 'Plant Collagen & Kashmiri Saffron Beauty Elixir',
    price: 2997.5,
    pv: 25,
    image: '/indusviva/products/iglow.jpeg',
    studioImage: '/indusviva/products/iglow.jpeg',
    originalImage: '/indusviva/products/iglow.jpeg',
    category: 'Skin & Radiance',
    categorySlug: 'beauty-glow',
    rating: 4.9,
    reviewCount: 318,
    badge: 'Beauty Essential',
    description:
      'iGlow is an ingestible Ayurvedic skincare formulation enriched with pure Kashmiri Saffron, plant collagen precursors, and potent antioxidant flavonoids. It works from the inside out to diminish hyperpigmentation, boost collagen elasticity, and impart a radiant glow.',
    highlights: [
      'Premium Kashmiri Saffron (Crocus sativus) & plant collagen cofactors',
      'Deep cellular hydration and support for skin elasticity',
      'Reduces dark spots, blemishes, and oxidative dermal stress',
      'Delicious ready-to-consume drink pouches',
    ],
    dosage: 'Take 1 pouch daily after breakfast or before bedtime.',
    order: 6,
  },
  {
    id: 58,
    slug: 'islim',
    name: 'iSlim',
    tagline: 'Smart Flat Tummies Nutritional Wellness Bar',
    price: 2997.5,
    pv: 25,
    image: '/indusviva/products/islim.jpeg',
    studioImage: '/indusviva/products/islim.jpeg',
    originalImage: '/indusviva/products/islim.jpeg',
    category: 'Weight Management',
    categorySlug: 'weight-management',
    rating: 4.8,
    reviewCount: 195,
    badge: 'Smart Nutrition',
    description:
      'iSlim is a patented nutritional wellness bar powered by the proprietary FB3 herbal complex. Designed to curb untimely hunger pangs, enhance lipid metabolism, and support a toned midsection without exhausting starvation diets.',
    highlights: [
      'Patented FB3 herbal extract blend (Coleus, Salacia, and Sesame)',
      'Curbs sugar cravings and regulates appetite naturally',
      'Aids in abdominal fat reduction and healthy weight balance',
      'Convenient, wholesome on-the-go evening nutritional meal bar',
    ],
    dosage: 'Consume 1 bar between 6:00 PM and 7:00 PM followed by 2 glasses of warm water.',
    order: 7,
  },
];

export function getIndusVivaProduct(idOrSlug: number | string): CatalogProduct | undefined {
  if (typeof idOrSlug === 'number' || !isNaN(Number(idOrSlug))) {
    const numId = Number(idOrSlug);
    return indusVivaProducts.find((p) => p.id === numId);
  }
  const clean = String(idOrSlug).toLowerCase().trim();
  return indusVivaProducts.find((p) => p.slug === clean || p.name.toLowerCase() === clean);
}

export function getDefaultIndusVivaLiveProducts(): LiveProduct[] {
  return indusVivaProducts.map((p) => ({
    id: p.id,
    product_name: p.name,
    thumbnail_url: p.image,
    pv: p.pv,
    selling_price: p.price,
    order_Sequence: p.order,
  }));
}
